/**
 * CreateProgrammeStepperPreview.tsx — the VER-128 Create Programme preview stepper (canvas
 * artboards 2a desktop / 3a mobile). Holds the in-progress `ProgrammeDraft` in local state
 * only; nothing here saves. See createProgramme.types.ts for what would need to change to
 * make step 1 (population) and the context-notes / case-note fields of step 4 real.
 */
import React, { useState } from "react";
import { Alert, Box, Button, Container, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useAppNavigate } from "../../../hooks/useAppNavigate";
import { DELIVERY_FORMATS, INITIAL_DRAFT, MOCK_COLLECTION, POPULATION_OPTIONS } from "./createProgramme.mock";
import { StepProgress } from "./StepProgress";
import { PopulationStep } from "./PopulationStep";
import { FormatStep } from "./FormatStep";
import { CollectionStep } from "./CollectionStep";
import { ScheduleStep } from "./ScheduleStep";
import { ReadyStep } from "./ReadyStep";

const TOTAL_STEPS = 5;
const CONTAINER_WIDTH: Record<number, "sm" | "md" | "lg"> = { 1: "md", 2: "md", 3: "lg", 4: "lg", 5: "md" };

export const CreateProgrammeStepperPreview: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { push } = useAppNavigate();

    const [step, setStep] = useState(1);
    const [draft, setDraft] = useState(INITIAL_DRAFT);

    const populationLabel = POPULATION_OPTIONS.find((option) => option.key === draft.population)?.title ?? "";
    const populationSummary = POPULATION_OPTIONS.find((option) => option.key === draft.population)?.summary ?? "";
    const formatOption = DELIVERY_FORMATS.find((option) => option.key === draft.deliveryFormat) ?? DELIVERY_FORMATS[0];

    const goNext = () => setStep((s) => Math.min(TOTAL_STEPS, s + 1));
    const goBack = () => setStep((s) => Math.max(1, s - 1));

    const selectCollection = () => {
        setDraft((d) => ({ ...d, collectionSelected: true }));
        goNext();
    };

    let stepContent: React.ReactNode;
    let footer: React.ReactNode;

    switch (step) {
        case 1:
            stepContent = (
                <PopulationStep
                    value={draft.population}
                    onChange={(population) => setDraft((d) => ({ ...d, population }))}
                />
            );
            footer = (
                <>
                    <Button variant="text" onClick={() => push("/programmes")}>
                        Cancel
                    </Button>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: { xs: "none", sm: "block" } }}
                        >
                            {populationSummary}
                        </Typography>
                        <Button variant="contained" endIcon={<ChevronRightIcon />} onClick={goNext}>
                            Continue
                        </Button>
                    </Stack>
                </>
            );
            break;
        case 2:
            stepContent = (
                <FormatStep
                    value={draft.deliveryFormat}
                    onChange={(deliveryFormat) => setDraft((d) => ({ ...d, deliveryFormat }))}
                />
            );
            footer = (
                <>
                    <Button variant="text" startIcon={<ChevronLeftIcon />} onClick={goBack}>
                        Back
                    </Button>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: { xs: "none", sm: "block" } }}
                        >
                            {formatOption.summary}
                        </Typography>
                        <Button variant="contained" endIcon={<ChevronRightIcon />} onClick={goNext}>
                            Continue
                        </Button>
                    </Stack>
                </>
            );
            break;
        case 3:
            stepContent = <CollectionStep collection={MOCK_COLLECTION} />;
            footer = (
                <>
                    <Button variant="text" startIcon={<ChevronLeftIcon />} onClick={goBack}>
                        Back
                    </Button>
                    <Stack direction="row" spacing={1.5}>
                        <Button variant="outlined">View another collection</Button>
                        <Button variant="contained" onClick={selectCollection}>
                            Select this collection
                        </Button>
                    </Stack>
                </>
            );
            break;
        case 4:
            stepContent = (
                <ScheduleStep
                    population={draft.population}
                    formatTitle={formatOption.title}
                    name={draft.name}
                    onNameChange={(name) => setDraft((d) => ({ ...d, name }))}
                    startDate={draft.startDate}
                    onStartDateChange={(startDate) => setDraft((d) => ({ ...d, startDate }))}
                    frequency={draft.frequency}
                    onFrequencyChange={(frequency) => setDraft((d) => ({ ...d, frequency }))}
                    contextNotes={draft.contextNotes}
                    onContextNotesChange={(contextNotes) => setDraft((d) => ({ ...d, contextNotes }))}
                    caseNoteFileName={draft.caseNoteFileName}
                    onCaseNoteFileChange={(caseNoteFileName) => setDraft((d) => ({ ...d, caseNoteFileName }))}
                />
            );
            footer = (
                <>
                    <Button variant="text" startIcon={<ChevronLeftIcon />} onClick={goBack}>
                        Back
                    </Button>
                    <Button variant="contained" endIcon={<ChevronRightIcon />} onClick={goNext}>
                        Create programme
                    </Button>
                </>
            );
            break;
        default:
            stepContent = (
                <ReadyStep
                    populationLabel={populationLabel}
                    formatTitle={formatOption.title}
                    nextAction={draft.nextAction}
                    onNextActionChange={(nextAction) => setDraft((d) => ({ ...d, nextAction }))}
                />
            );
            footer = (
                <Button variant="text" startIcon={<ChevronLeftIcon />} onClick={goBack}>
                    Back
                </Button>
            );
    }

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Box flex="0 0 auto" px={{ xs: 2, md: 4 }} pt={{ xs: 2, md: 2.5 }} pb={0}>
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                    Design preview (VER-128) — nothing on this screen is saved.
                </Alert>
                <Typography variant="h4" component="h1" color="secondary.main" gutterBottom>
                    Create Programme
                </Typography>
            </Box>

            <Box flex="1 1 auto" minHeight={0} overflow="auto" px={{ xs: 2, md: 4 }} py={3} pb={isMobile ? 12 : 3}>
                <Container maxWidth={CONTAINER_WIDTH[step]} disableGutters>
                    <Stack spacing={3.5}>
                        <StepProgress
                            currentStep={step}
                            totalSteps={TOTAL_STEPS}
                            variant={isMobile ? "mobile" : "desktop"}
                        />
                        {stepContent}
                    </Stack>
                </Container>
            </Box>

            <Box
                flex="0 0 auto"
                px={{ xs: 2, md: 4 }}
                py={2}
                sx={{
                    borderTop: "1px solid",
                    borderColor: "borderShade.main",
                    bgcolor: "background.paper",
                    position: isMobile ? "sticky" : "static",
                    bottom: 0,
                }}
            >
                <Stack
                    direction={{ xs: step === 3 ? "column" : "row", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                    spacing={1.5}
                >
                    {footer}
                </Stack>
            </Box>
        </Box>
    );
};
